import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
    Flex, Text, Link, Icon
} from 'components/ui';
import { colors, space } from 'style/config';
import { wrapFunctionalComponent } from 'utils/framework';
import Actions from 'Actions';
import { renderModal, globalModals } from 'utils/globalModals';
import { globalModalsSelector } from 'selectors/page/headerFooterTemplate/data/globalModals/globalModalsSelector';
import bccUtils from 'utils/BCC';
import deliveryOptionsUtils from 'utils/DeliveryOptions';
import languageLocale from 'utils/LanguageLocale';
import FulfillmentMethodsEN from 'components/ProductPage/FulfillmentMethods/locales/FulfillmentMethods_en_US';
import FulfillmentMethodsFR from 'components/ProductPage/FulfillmentMethods/locales/FulfillmentMethods_fr_CA';

const { BOPIS_SERVICE_INFO_FAQS } = globalModals;
const { BOPIS_INFO } = bccUtils.MEDIA_IDS;
const getText = languageLocale.getLocalisedDataFromResource(FulfillmentMethodsEN, FulfillmentMethodsFR);

const styles = {
    infoButton: {
        appearance: 'none',
        background: 'transparent',
        border: 0,
        cursor: 'pointer',
        padding: space[1],
        margin: -space[1],
        display: 'inline-flex',
        alignItems: 'center',
        color: colors.gray
    }
};

/**
 * Builds the header row's display values. Extracted from the component body
 * so the SHP-5280 nearby-store-vs-selected-store branching doesn't push
 * `PickupDetailsPanel` over the complexity budget.
 *
 * @param {object} params
 * @returns {{headingLabel: string, headingColor: string|undefined, headingLinkText: string, headingLinkAriaLabel: string, headingDataAt: string|undefined}}
 */
const buildHeadingDisplay = ({
    showNearbyStoreAvailability, isInStockAtNearbyStore, availabilityLabel, availabilityColor, storeNameText
}) => {
    if (!showNearbyStoreAvailability) {
        return {
            headingLabel: getText(availabilityLabel),
            headingColor: availabilityColor,
            headingLinkText: storeNameText,
            // The store name is the link's visible text, so it has to lead the accessible
            // name too — otherwise VoiceOver announces only "Change pickup store" and the
            // selected store is never read (SHP-5526, WCAG 2.5.3 Label in Name).
            headingLinkAriaLabel: getText('pickupPanelChangeStoreAriaLabel', [storeNameText]),
            headingDataAt: undefined
        };
    }

    return {
        headingLabel: getText(isInStockAtNearbyStore ? 'inStock' : 'outOfStock'),
        headingColor: isInStockAtNearbyStore ? 'green' : 'red',
        headingLinkText: getText('nearbyStores'),
        headingLinkAriaLabel: getText('nearbyStoresLinkAriaLabel'),
        headingDataAt: isInStockAtNearbyStore ? 'in_stock_at_nearby_stores_label' : 'out_of_stock_at_nearby_stores_label'
    };
};

/**
 * PickupDetailsPanel renders the Pickup selected-state details for the redesigned
 * PDP fulfillment selector (SHP-5238). It shows the preferred-store availability
 * header ("In Stock at {store}") with a chevron link to change the store, the
 * green readiness message from the API (optionally with an order-cutoff countdown),
 * the available pickup methods (in-store / curbside / concierge), and an info
 * button that opens the About Buy Online & Pick Up modal.
 *
 * When no preferred store is set `storeName` is empty; the store link falls back to
 * the localized "stores near you" copy so the header never renders a blank link,
 * mirroring the legacy BOPIS behavior.
 *
 * @param {object} props
 * @param {object} props.currentProduct - The current product (passed to the store selector modal).
 * @param {string} props.storeName - Display name of the preferred store (empty when none is set).
 * @param {string} props.availabilityLabel - Locale key for the availability status (e.g. 'inStock').
 * @param {string} props.availabilityColor - Text color token for the availability label.
 * @param {string} props.pickupMessage - Readiness message from the API.
 * @param {string} props.pickupMessageColor - Text color token for the readiness message.
 * @param {boolean} props.displayOrderCutoffCountdown - Whether to convert the readiness message into a live countdown.
 * @param {boolean} props.showInStorePickup - Whether the in-store pickup indicator is shown.
 * @param {boolean} props.showCurbsideIndicator - Whether the curbside pickup indicator is shown.
 * @param {boolean} props.showConciergeCurbsideIndicator - Whether the curbside concierge indicator is shown.
 * @param {boolean} props.isPickupOutOfStock - Whether the item is out of stock in selected store.
 * @param {boolean} props.showNearbyStoreAvailability - Whether to show the nearby-store availability
 *   row in the header instead of the selected-store line (SHP-5280; item OOS at the selected store
 *   with Multi-Store Filtering on and nearby data present).
 * @param {boolean} props.isInStockAtNearbyStore - Whether the item is in stock at a nearby store
 *   (drives the green/red treatment of the nearby row).
 * @param {boolean} props.isPickupUnavailableAtStore - SHP-5361: the preferred store doesn't offer
 *   BOPIS at all. Overrides the readiness message/pickup-method rows with "Pickup not offered at
 *   this store"; the store-availability header link is unaffected (still selectable/changeable).
 * @param {boolean} props.isPickupUnavailableForItem - SHP-5361: the preferred store offers BOPIS,
 *   but this SKU isn't pickup-eligible there. Overrides the readiness message/pickup-method rows
 *   with "Pickup not offered for this item".
 * @returns {JSX.Element}
 */
const PickupDetailsPanel = ({
    currentProduct,
    storeName,
    availabilityLabel,
    availabilityColor,
    pickupMessage,
    pickupMessageColor,
    displayOrderCutoffCountdown,
    showInStorePickup,
    showCurbsideIndicator,
    showConciergeCurbsideIndicator,
    isPickupOutOfStock,
    showNearbyStoreAvailability,
    isInStockAtNearbyStore,
    isPickupUnavailableAtStore,
    isPickupUnavailableForItem
}) => {
    const dispatch = useDispatch();
    const globalModalsData = useSelector(globalModalsSelector);
    const aboutBopisModal = globalModalsData[BOPIS_SERVICE_INFO_FAQS];

    const storeNameText = storeName || getText('storesNearYou');
    // When OOS at the selected store with nearby data (SHP-5280), the header line becomes the
    // nearby-store availability row; otherwise it shows the selected-store availability.
    const {
        headingLabel, headingColor, headingLinkText, headingLinkAriaLabel, headingDataAt
    } = buildHeadingDisplay({
        showNearbyStoreAvailability,
        isInStockAtNearbyStore,
        availabilityLabel,
        availabilityColor,
        storeNameText
    });
    const readinessMessage =
        pickupMessage && displayOrderCutoffCountdown ? deliveryOptionsUtils.getBOPISCountdownMessage(pickupMessage) : pickupMessage;
    // SHP-5361: takes priority over the OOS "Check other stores" link and the normal readiness
    // message — BOPIS eligibility (store- or item-level) is a more fundamental blocker than stock.
    const unavailableMessage = isPickupUnavailableAtStore
        ? getText('pickupNotOfferedAtStore')
        : isPickupUnavailableForItem
            ? getText('pickupNotOfferedForItem')
            : undefined;
    const pickupMethods = [
        showInStorePickup && getText('inStorePickup'),
        showCurbsideIndicator && getText('curbsidePickup'),
        showConciergeCurbsideIndicator && getText('curbsideConcierge')
    ].filter(Boolean);
    // SHP-5453: the message and pickup-method rows share a 2px-gap wrapper so the panel's 8px
    // header gap lands once, and is dropped entirely when neither renders (else it trails).
    const showPickupMethods = pickupMethods.length > 0 && !isPickupOutOfStock && !unavailableMessage;
    const hasMessageRow = Boolean(unavailableMessage || isPickupOutOfStock || readinessMessage);

    const handleStoreClick = useCallback(
        e => {
            deliveryOptionsUtils.showStoreListModal.call({ props: { currentProduct, reserveAndPickup: true } }, e, { isFromChangeMethod: true });
        },
        [currentProduct]
    );

    const handleInfoClick = useCallback(
        e => {
            e?.preventDefault();
            e?.stopPropagation();
            renderModal(aboutBopisModal, () => {
                dispatch(
                    Actions.showMediaModal({
                        isOpen: true,
                        mediaId: BOPIS_INFO,
                        titleDataAt: 'informationModalTitle'
                    })
                );
            });
        },
        [dispatch, aboutBopisModal]
    );

    return (
        <Flex
            flexDirection='column'
            gap={2}
            data-at={Sephora.debug.dataAt('pickup_details_panel')}
        >
            <Flex
                alignItems='center'
                justifyContent='space-between'
                width='100%'
            >
                <Text
                    is='p'
                    fontSize='sm'
                    data-at={Sephora.debug.dataAt(headingDataAt)}
                >
                    <Text
                        is='span'
                        fontWeight='bold'
                        color={headingColor}
                        children={headingLabel}
                    />
                    <Text
                        is='span'
                        color='black'
                        children={` ${getText('pickupPanelAt')} `}
                    />
                    <Link
                        onClick={handleStoreClick}
                        arrowDirection='down'
                        fontWeight='bold'
                        padding={2}
                        margin={-2}
                        aria-label={headingLinkAriaLabel}
                        children={headingLinkText}
                    />
                </Text>
                <button
                    type='button'
                    onClick={handleInfoClick}
                    aria-label={getText('pickupPanelInfoAriaLabel')}
                    css={styles.infoButton}
                >
                    <Icon
                        name='infoOutline'
                        size={16}
                    />
                </button>
            </Flex>
            {(hasMessageRow || showPickupMethods) && (
                <Flex
                    flexDirection='column'
                    gap='2px'
                >
                    {unavailableMessage ? (
                        <Text
                            is='p'
                            fontSize='sm'
                            color='gray'
                            children={unavailableMessage}
                        />
                    ) : isPickupOutOfStock ? (
                        <Link
                            onClick={handleStoreClick}
                            color={colors.blue}
                            fontSize='sm-bg'
                            width='fit-content'
                        >
                            {getText('checkOtherStores')}
                        </Link>
                    ) : (
                        readinessMessage && (
                            <Text
                                is='p'
                                fontSize='sm'
                                color={pickupMessageColor}
                                children={readinessMessage}
                            />
                        )
                    )}
                    {showPickupMethods && (
                        <Flex
                            alignItems='center'
                            fontSize='sm'
                            color='gray'
                            gap={2}
                            flexWrap='wrap'
                        >
                            {pickupMethods.map(label => (
                                <Flex
                                    key={label}
                                    gap={1}
                                    alignItems='center'
                                >
                                    <Icon
                                        name='checkmark'
                                        size={10}
                                    />
                                    {label}
                                </Flex>
                            ))}
                        </Flex>
                    )}
                </Flex>
            )}
        </Flex>
    );
};

PickupDetailsPanel.propTypes = {
    currentProduct: PropTypes.object,
    storeName: PropTypes.string,
    availabilityLabel: PropTypes.string,
    availabilityColor: PropTypes.string,
    pickupMessage: PropTypes.string,
    pickupMessageColor: PropTypes.string,
    displayOrderCutoffCountdown: PropTypes.bool,
    showInStorePickup: PropTypes.bool,
    showCurbsideIndicator: PropTypes.bool,
    showConciergeCurbsideIndicator: PropTypes.bool,
    isPickupOutOfStock: PropTypes.bool,
    showNearbyStoreAvailability: PropTypes.bool,
    isInStockAtNearbyStore: PropTypes.bool,
    isPickupUnavailableAtStore: PropTypes.bool,
    isPickupUnavailableForItem: PropTypes.bool
};

export default wrapFunctionalComponent(PickupDetailsPanel, 'PickupDetailsPanel');
